import { EDatasourceType } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { DomainAccessModalActionTypes } from "../../../store/domain-access-modal/types";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import {
  KuikaPopupContainerContext,
  type KuikaPopupContainerGetter
} from "../../ui-kit/kuika-popup-container/kuika-popup-container-context";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwap from "../../ui-kit/kuika-swap/kuika-swap";
import "./connector-picker-popover.scss";
import { CSHARP_TEMPLATE_OPTIONS, GATED_CSHARP_TEMPLATE_ACCESS, GATED_CSHARP_TEMPLATES } from "./csharp-templates";
import { createDatasource, CreateDatasourceOutcome } from "./datasource-create-actions";
import { getDatasourceNameFormatError } from "./datasource-name-validation";
import {
  ConnectorFocusKey,
  DATASOURCE_CARD_TINTS,
  DatasourceCreateCategory,
  getConnectorColumns,
  getConnectorReadingOrder,
  IConnectorColumn,
  IConnectorSection,
  IDatasourceCardTint,
  IDatasourceConnector,
  moveConnectorFocus
} from "./datasources-landing-model";

/** CREATE makes a datasource, so its button says so while it works: "Creating", then "Created". */
export const CREATE_DATASOURCE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

/**
 * "Which connector?" as a popover anchored to whatever opened it, no page dimming. The three
 * landing-page create cards (Database / API / Function) each open it on their own category; the
 * Sources tree's "+" opens it on `"all"`: the three cards' lists side by side, one column each.
 * The caller passes its own trigger as `children` (Radix `Popover.Trigger asChild`, so it must
 * forward its ref). `ConnectorPickerModal` is unchanged and still serves the openers that ask for
 * a category through the provider instead: a switcher chip's "create new"
 * (`connection-switcher-chip.tsx`, `code-datasource-switcher-chip.tsx`).
 *
 * Two steps in one popover, swapped with `KuikaSwap` rather than two surfaces: picking a provider
 * is the same decision as naming it, the same way the modal treated them (reveal-in-place, not a
 * second screen) — this just also lets you go back.
 */

type PopoverStep = "pick" | "name";

const rowId = (category: DatasourceCreateCategory, connector: IDatasourceConnector): string =>
  `connector-popover-${category}-${connector.type}`;

const POPOVER_CLASS: string = "kuika-connector-popover";

/**
 * Where the template select's options mount: inside the popover. Portaled to <body> they would sit
 * outside the popover's DOM, so the click that picks a template would read as a click outside it
 * and close the whole thing.
 */
const popoverContainer: KuikaPopupContainerGetter = (triggerNode?: HTMLElement): HTMLElement =>
  triggerNode?.closest<HTMLElement>(`.${POPOVER_CLASS}`) ?? document.body;

/** Letters only — a badge like ".NET 10" never leaks into the fallback tile. */
const getMonogram = (label: string): string => {
  const letters: string = label.replace(/[^A-Za-z0-9]/g, "");
  return letters.slice(0, 2).toUpperCase() || "?";
};

const ARROW_KEYS: readonly string[] = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

const isArrowKey = (key: string): key is ConnectorFocusKey => ARROW_KEYS.includes(key);

export interface IConnectorPickerPopoverOwnProps {
  /** One card's connectors, or `"all"`: every card's, one column each. */
  category: DatasourceCreateCategory;
  /** What the provider list is called for assistive tech: the trigger's own name. */
  label: string;
  /** The element the popover anchors to and toggles from. Must forward its ref (Radix `asChild`). */
  children: React.ReactElement;
  /** Kept between the popover and the viewport edges; the wide `"all"` popover can reach them. */
  collisionPadding?: number;
  sd: IScreenDesignContextState;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface IConnectorPickerPopoverDispatchProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

type IConnectorPickerPopoverProps = IConnectorPickerPopoverOwnProps & IConnectorPickerPopoverDispatchProps;

const ConnectorPickerPopover = ({
  category,
  label,
  children,
  collisionPadding,
  sd,
  open,
  onOpenChange,
  showDomainAccessRequiredModal: requireDomainAccess
}: IConnectorPickerPopoverProps): JSX.Element => {
  const menuRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const nameInputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<PopoverStep>("pick");
  const [selectedType, setSelectedType] = useState<EDatasourceType | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [nameTouched, setNameTouched] = useState<boolean>(false);
  // C# only: which class the new function is seeded with — the plain one, or a provider.
  const [template, setTemplate] = useState<EDatasourceType>(EDatasourceType.CSharpFunctions);
  const [saveStatus, setSaveStatus] = useState<KuikaButtonStatus>("idle");

  const saveMachineRef: React.MutableRefObject<SaveStatus | null> = useRef<SaveStatus | null>(null);
  if (saveMachineRef.current === null) {
    saveMachineRef.current = new SaveStatus(setSaveStatus);
  }
  const saveMachine: SaveStatus = saveMachineRef.current;
  useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);

  const columns: IConnectorColumn[] = useMemo(
    (): IConnectorColumn[] => getConnectorColumns(category, sd?.project?.projectType),
    [category, sd?.project?.projectType]
  );
  // Every index below (the active row, Enter's target) counts in this: the drawn order.
  const connectors: IDatasourceConnector[] = useMemo(
    (): IDatasourceConnector[] => getConnectorReadingOrder(columns),
    [columns]
  );
  const isColumns: boolean = columns.length > 1;

  const selectedConnector: IDatasourceConnector | undefined = connectors.find(
    (connector: IDatasourceConnector): boolean => connector.type === selectedType
  );

  // A fresh sheet every time it opens: the previous pick, the previous name and the previous
  // ✓/✗ are not this decision. `ConnectorPickerModal.componentDidUpdate` does the same on `visible`.
  useEffect((): void => {
    if (!open) return;
    setStep("pick");
    setSelectedType(undefined);
    setActiveIndex(0);
    setName("");
    setNameTouched(false);
    setTemplate(EDatasourceType.CSharpFunctions);
    saveMachine.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveMachine is a stable ref, not a reactive input
  }, [open]);

  // Focus follows the step: the list the instant the popover opens (a click did not focus a row)
  // and again after Escape steps back, the name field when a provider is picked. Not `autoFocus`
  // on the field: `KuikaSwap` keeps the outgoing step mounted while it leaves, so a name field
  // leaving on its way back to the list would take the focus straight back from the list.
  useEffect((): void => {
    if (!open) return;
    if (step === "pick") {
      menuRef.current?.focus();
    } else {
      nameInputRef.current?.focus();
    }
  }, [open, step]);

  // The active row moves by `activeIndex` alone, not real DOM focus (`aria-activedescendant`), so
  // nothing scrolls it into view on its own — the Function card's list is tall enough that an
  // arrow press can walk the highlight straight out of the visible 320px, and the columns out of
  // a short viewport.
  useEffect((): void => {
    if (!open || step !== "pick") return;
    const connector: IDatasourceConnector | undefined = connectors[activeIndex];
    if (!connector) return;
    document.getElementById(rowId(category, connector))?.scrollIntoView({ block: "nearest" });
  }, [open, step, activeIndex, connectors, category]);

  const pick = (connector: IDatasourceConnector): void => {
    if (connector.accessName && KMainFunctions.isDomainHasAccess(connector.accessName) === false) {
      requireDomainAccess(connector.accessName);
      return;
    }
    setSelectedType(connector.type);
    setStep("name");
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (connectors.length === 0) return;

    const key: string = event.key;
    if (isArrowKey(key)) {
      event.preventDefault();
      setActiveIndex((current: number): number => moveConnectorFocus(columns, current, key));
      return;
    }
    if (event.key === "Enter") {
      const connector: IDatasourceConnector | undefined = connectors[activeIndex];
      if (connector) {
        event.preventDefault();
        pick(connector);
      }
    }
  };

  // Escape's default (Radix) is to close the whole popover — right for step 1, wrong for step 2,
  // where it should only back out of the name field. `preventDefault` here is what stops the
  // close; `KuikaPopover` forwards it straight to Radix's own content.
  const handleEscapeKeyDown = (event: KeyboardEvent): void => {
    if (step === "name") {
      event.preventDefault();
      setStep("pick");
    }
  };

  const handleBack = (): void => {
    setStep("pick");
  };

  // Same gate as the modal's: a provider template without its subscription is refused, not hidden.
  const handleTemplateChange = (value: EDatasourceType): void => {
    if (
      GATED_CSHARP_TEMPLATES.includes(value) &&
      KMainFunctions.isDomainHasAccess(GATED_CSHARP_TEMPLATE_ACCESS) === false
    ) {
      requireDomainAccess(GATED_CSHARP_TEMPLATE_ACCESS);
      return;
    }
    setTemplate(value);
  };

  const handleCancel = (): void => {
    onOpenChange(false);
  };

  const formatError: string | undefined = getDatasourceNameFormatError(name);
  const isNameValid: boolean = formatError === undefined;

  const handleCreate = async (): Promise<void> => {
    if (selectedType === undefined) return;
    if (!isNameValid) {
      setNameTouched(true);
      return;
    }
    if (!saveMachine.canStart) return;

    saveMachine.start();
    const outcome: CreateDatasourceOutcome = await createDatasource({
      sd,
      type: selectedType,
      name,
      template,
      openCreated: (openFn: () => void): void => {
        saveMachine.succeed((): void => {
          openFn();
          onOpenChange(false);
        });
      }
    });

    if (outcome === "rejected") {
      saveMachine.fail();
    } else if (outcome === "connection-panel") {
      saveMachine.reset();
      onOpenChange(false);
    }
  };

  // Every tile wears its card's colour at rest, not only on the active row: the colour says which
  // card a provider belongs to (the "+" popover's columns read as the three cards), while the
  // row's own fill says which one is active. Inline, because the menu item's resting and selected
  // icon colours would otherwise override it — `KuikaMenuItem` lets an icon's own colour win.
  const renderTile = (connector: IDatasourceConnector): React.ReactNode => {
    const tint: IDatasourceCardTint = DATASOURCE_CARD_TINTS[connector.card];
    return (
      <span
        className="kuika-connector-popover__tile"
        style={
          {
            "--kuika-connector-tile-bg": tint.background,
            "--kuika-connector-tile-fg": tint.color
          } as React.CSSProperties
        }
      >
        {connector.iconName ? (
          <KuikaSvg
            type="datasource-icons"
            name={connector.iconName}
            width={24}
            height={24}
            color="var(--kuika-connector-tile-fg)"
          />
        ) : (
          <span className="kuika-connector-popover__monogram">{getMonogram(connector.label)}</span>
        )}
      </span>
    );
  };

  const renderRow = (connector: IDatasourceConnector): React.ReactNode => {
    const index: number = connectors.indexOf(connector);
    const isActive: boolean = index === activeIndex;
    // The modal's own two testids on the same entry (outer item, then its label) — kept verbatim
    // so an existing selector finds the row whichever surface rendered it.
    const nameTestId: string = `${connector.label.toLowerCase().replace(/\s/g, "")}Selection`;

    return (
      <KuikaMenuItem
        key={connector.type}
        id={rowId(category, connector)}
        role="option"
        aria-selected={isActive}
        // KuikaMenuItem marks its selected row aria-current; an option says it with aria-selected.
        aria-current={undefined}
        tabIndex={-1}
        data-testid={`${connector.type}Selection`}
        leftIcon={renderTile(connector)}
        title={
          <span data-testid={nameTestId}>
            {connector.label}
            {connector.badges?.map((badge: string) => (
              <span key={badge} className="kuika-connector-popover__badge">
                {badge}
              </span>
            ))}
          </span>
        }
        subtitle={connector.description}
        selected={isActive}
        onClick={(): void => pick(connector)}
        onMouseEnter={(): void => setActiveIndex(index)}
      />
    );
  };

  const activeConnector: IDatasourceConnector | undefined = connectors[activeIndex];

  const renderSection = (section: IConnectorSection): React.ReactNode => (
    <div className="kuika-connector-popover__section" key={section.title}>
      <span
        className="kuika-connector-popover__section-title"
        // The modal's own id on the same heading, like the rows' ids: a selector for it finds
        // it whichever surface opened.
        data-testid={section.title === "Databases" ? "databaseCategoryTitle" : undefined}
      >
        {section.title}
      </span>
      {section.connectors.map(renderRow)}
    </div>
  );

  const renderPick = (): React.ReactNode => (
    <>
      <div
        ref={menuRef}
        className="kuika-connector-popover__menu"
        role="listbox"
        aria-label={label}
        tabIndex={0}
        aria-activedescendant={activeConnector ? rowId(category, activeConnector) : undefined}
        onKeyDown={handleMenuKeyDown}
        data-testid="connectorPopoverMenu"
      >
        {connectors.length === 0 && (
          <div className="kuika-connector-popover__empty">No providers available for this project.</div>
        )}
        {columns.map((column: IConnectorColumn) => (
          <div className="kuika-connector-popover__column" key={column.card}>
            {column.sections.map(renderSection)}
          </div>
        ))}
      </div>
      <div className="kuika-connector-popover__footer">
        <span className="kuika-connector-popover__footer-hint">
          <span className="kuika-connector-popover__key-chip">↑</span>
          <span className="kuika-connector-popover__key-chip">↓</span>
          {isColumns && <span className="kuika-connector-popover__key-chip">←</span>}
          {isColumns && <span className="kuika-connector-popover__key-chip">→</span>}
          to move
        </span>
        <span className="kuika-connector-popover__footer-hint">
          <span className="kuika-connector-popover__key-chip">Enter</span>
          to choose
        </span>
      </div>
    </>
  );

  const nameInputId: string = `connector-popover-name-${category}`;

  const renderName = (): React.ReactNode => (
    <div className="kuika-connector-popover__name" data-testid="createDatasourcePanel">
      <KuikaButton
        type="text"
        size="sm"
        className="kuika-connector-popover__back"
        onClick={handleBack}
        data-testid="connectorPopoverBackButton"
      >
        <KuikaSvg type="arrows" name="chevron-left" width={14} height={14} />
        {selectedConnector?.label}
      </KuikaButton>
      <label className="kuika-connector-popover__label" htmlFor={nameInputId}>
        Name
      </label>
      <KuikaInput
        id={nameInputId}
        data-testid="datasourceNameInput"
        placeholder="Enter a name"
        ref={nameInputRef}
        size="md"
        autoComplete="off"
        value={name}
        error={nameTouched && !isNameValid}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void => {
          setName(String(value ?? ""));
          setNameTouched(true);
        }}
        onBlur={(): void => setNameTouched(true)}
        onPressEnter={(): void => void handleCreate()}
      />
      {nameTouched && formatError && <span className="kuika-connector-popover__error">{formatError}</span>}
      {selectedType === EDatasourceType.CSharpFunctions && (
        <>
          <span className="kuika-connector-popover__label">Template</span>
          <KuikaSelectbox
            size="md"
            placeholder="Select a Template"
            value={template}
            options={CSHARP_TEMPLATE_OPTIONS}
            onChange={(value: EDatasourceType): void => handleTemplateChange(value)}
            data-testid="datasourceTemplateSelect"
          />
        </>
      )}
      <div className="kuika-connector-popover__actions">
        <KuikaButton
          type="tertiary"
          color="neutral"
          size="md"
          uppercase
          onClick={handleCancel}
          data-testid="cancelCreateDatasourceButton"
        >
          CANCEL
        </KuikaButton>
        <KuikaButton
          type="primary"
          size="md"
          uppercase
          disabled={!isNameValid}
          status={saveStatus}
          statusLabels={CREATE_DATASOURCE_STATUS_LABELS}
          onClick={(): void => void handleCreate()}
          data-testid="createDatasourceButton"
        >
          CREATE
        </KuikaButton>
      </div>
    </div>
  );

  // No aria-haspopup / aria-expanded / aria-controls on the trigger here: Radix's
  // `Popover.Trigger` writes all three onto `children`, pointing at its own content id.
  return (
    <KuikaPopover
      className={classNames(POPOVER_CLASS, `${POPOVER_CLASS}--${step}`, { [`${POPOVER_CLASS}--columns`]: isColumns })}
      position="bottom"
      align="start"
      sideOffset={8}
      collisionPadding={collisionPadding}
      hideCloseIcon
      visible={open}
      onVisibleChange={onOpenChange}
      onEscapeKeyDown={handleEscapeKeyDown}
      content={
        <KuikaPopupContainerContext.Provider value={popoverContainer}>
          <div className="kuika-connector-popover__content">
            <KuikaSwap contentKey={step} as="div" leaving="inline">
              {step === "pick" ? renderPick() : renderName()}
            </KuikaSwap>
          </div>
        </KuikaPopupContainerContext.Provider>
      }
    >
      {children}
    </KuikaPopover>
  );
};

const mapDispatchToProps = (
  dispatch: Dispatch<DomainAccessModalActionTypes>
): IConnectorPickerPopoverDispatchProps => ({
  showDomainAccessRequiredModal: (actionName: string | string[]): void => {
    dispatch(showDomainAccessRequiredModal(actionName));
  }
});

const connectorPickerPopover = connect(null, mapDispatchToProps)(ConnectorPickerPopover);

export { connectorPickerPopover as ConnectorPickerPopover };
